import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { X } from "lucide-react";

interface Toast {
  id: number;
  message: string;
  level: "error" | "warning" | "success";
}

interface ToastCtx {
  toast: (message: string, level?: Toast["level"]) => void;
}

const Ctx = createContext<ToastCtx>({ toast: () => {} });

export const useToast = () => useContext(Ctx);

let _nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, level: Toast["level"] = "error") => {
    const id = _nextId++;
    setToasts((prev) => [...prev.slice(-4), { id, message, level }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 6000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const levelStyles: Record<Toast["level"], string> = {
    error: "bg-red-950/90 border-red-500/40 text-red-300",
    warning: "bg-yellow-950/90 border-yellow-500/40 text-yellow-300",
    success: "bg-emerald-950/90 border-emerald-500/40 text-emerald-300",
  };

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-start gap-2 px-4 py-3 rounded-xl border text-sm font-medium shadow-lg animate-in slide-in-from-right ${levelStyles[t.level]}`}
          >
            <span className="flex-1">{t.message}</span>
            <button onClick={() => dismiss(t.id)} className="shrink-0 opacity-60 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
